import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import CorvinArt from '../brand/CorvinArt';
import Button from '../common/Button';
import { Modal } from '../common/Modal';
import corvinAPI from '../../services/corvinApi';
import { useAuth } from '../auth/AuthProvider';
import { readSession, writeSession } from './session';

const reducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return true;
  }
};

// Corvin's proactive tip: one per browser session, about 2 s after the first
// page renders, so it never blocks the page. On 204 or any error he says
// nothing. See docs/superpowers/specs/2026-10-03-crow-logo-and-corvin-design.md.
export default function Corvin({ delayMs = 2000 }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [session, setSession] = useState(() => readSession(userId));
  // A tip restored from this session is already in place: no settle.
  const [entered, setEntered] = useState(() => Boolean(readSession(userId)));
  const [blink, setBlink] = useState(false);

  const update = useCallback((next) => {
    const tagged = { ...next, userId };
    writeSession(tagged);
    setSession(tagged);
  }, [userId]);

  useEffect(() => {
    if (session) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      let tip = null;
      try {
        tip = await corvinAPI.getTip();
      } catch {
        tip = null;
      }
      if (!cancelled) update({ tip, closed: false });
    }, delayMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once per mount, gated by the session

  const tip = session && !session.closed ? session.tip : null;
  const close = useCallback(() => update({ ...session, closed: true }), [session, update]);
  const dismiss = useCallback(() => {
    // Best effort: a failed dismiss just means the tip may return next session.
    Promise.resolve().then(() => corvinAPI.dismiss(tip.key)).catch(() => {});
    close();
  }, [tip, close]);

  // One settle on first appearance.
  useEffect(() => {
    if (!tip || entered) return undefined;
    const t = setTimeout(() => setEntered(true), 20);
    return () => clearTimeout(t);
  }, [tip, entered]);

  // One blink, a moment after he appears.
  useEffect(() => {
    if (!tip || tip.kind === 'welcome' || reducedMotion()) return undefined;
    const shut = setTimeout(() => setBlink(true), 700);
    const open = setTimeout(() => setBlink(false), 850);
    return () => {
      clearTimeout(shut);
      clearTimeout(open);
    };
  }, [tip]);

  // Escape closes the bubble for this session; the tip is not dismissed.
  useEffect(() => {
    if (!tip || tip.kind === 'welcome') return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [tip, close]);

  if (!tip) return null;

  if (tip.kind === 'welcome') {
    // Closing it any way (button, ✕, Escape, overlay) records it as seen.
    return (
      <Modal isOpen onClose={dismiss} title={tip.title} size="sm">
        <div className="flex flex-col items-center gap-4 text-center">
          <CorvinArt pose="waving" size={140} />
          <p className="text-body text-ink-soft">{tip.body}</p>
          <Button onClick={dismiss}>{tip.action_label}</Button>
        </div>
      </Modal>
    );
  }

  return (
    <aside
      aria-label="Tip from Corvin"
      className={`fixed right-4 z-40 bottom-[calc(5rem+env(safe-area-inset-bottom))] lg:bottom-6
        w-[calc(100%-2rem)] max-w-sm motion-safe:transition motion-safe:duration-overlay ease-out
        ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
    >
      <div role="status" aria-live="polite"
        className="flex gap-3 rounded-card border border-border-default bg-surface-card p-4 shadow-popover">
        <CorvinArt pose={blink ? 'avatar-blink' : 'avatar'} size={44} className="flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-body-strong text-ink">{tip.title}</p>
          <p className="mt-0.5 text-small text-ink-muted">{tip.body}</p>
          {tip.action_href && (
            <Link
              to={tip.action_href}
              onClick={close}
              className="mt-3 inline-flex min-h-touch items-center rounded-control bg-accent px-3 text-small font-medium text-white hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              {tip.action_label}
            </Link>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss this tip for 14 days"
          className="-m-1 inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-control text-ink-muted hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
}
